import { Alert } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { AdminProcess } from '@frontend/components/pages/processes/adminProcessDetailsTypes';
import {
    AnalystOrganizationType,
    CustomControlStatus,
    DiaryTypes,
    EventEcgCardioExaminationDuration,
    HolterCardioExaminationDuration,
    ManagerAuthorization,
    ProcedureStatus,
    ProcessEntity,
    RecorderType,
} from '@common/enums';
import { isPatientOlderThanEighteenOnDate } from '@common/utils/isPatientOlderThanGivenAgeOnDate';
import {
    AdminProcessNullableFormattedDatetime,
    AnchorButton,
    Button,
    FormDivider,
    FormParagraph,
} from '@frontend/components/elements/common';
import { FormLabel } from '@frontend/components/elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '@frontend/components/elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '@frontend/components/elements/forms/InputContainer';
import { PractitionerAndOrganizationActors } from '@frontend/components/pages/processes/PractitionerAndOrganizationActors';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';

export function TcohV2AdminDetails(props: AdminProcess) {
    const [holterAnalystOrganization, setHolterAnalystOrganization] = useState<{
        id: string;
        name: string;
        txcsId: string | null;
    }>();

    const hasButtons = hasOneAuthorization(ManagerAuthorization.adminProcessEdit);
    const [fillInUploadJsonAlert, setFillInUploadJsonAlert] = useState(false);

    if (!props.procedure || !props.procedure.customControlData) {
        return null;
    }

    useEffect(() => {
        retrieveHolterAnalystOrganization();
    }, []);

    return (
        <>
            <FormDivider />
            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.coupledRecorder')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{getRecorderDescription()}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.durationExamination')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{getDuration()}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.dateConfigured')}</FormLabel>
                <InputContainer>
                    <FormParagraph>
                        <AdminProcessNullableFormattedDatetime
                            value={props.procedure.customControlData.configurationDatetime}
                        />
                    </FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.dateUploadStarted')}</FormLabel>
                <InputContainer>
                    <FormParagraph>
                        <AdminProcessNullableFormattedDatetime
                            value={props.procedure.customControlData.downloadedDatetime}
                        />
                    </FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.dateUploadFinished')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{getSentData()}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.diary')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{getDiaryType()}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.attachments')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{getFileCount(props.procedure.customControlData)}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>

            {showNewUploadReport() && (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('processDetails.newUploadReport')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            <AdminProcessNullableFormattedDatetime
                                value={props.procedure.customControlData.files[0].resendDateTime}
                            />
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            )}

            {renderAnalyst()}
            {renderGenerateConfiguredByKsyos()}
            {renderGenerateUploadJson()}
        </>
    );

    function renderAnalyst() {
        let canBeAnalyzedByAnalystFromKsyos = false;
        if (
            props.procedure &&
            props.procedure.customControlData &&
            props.procedure.customControlData.configurationDatetime
        ) {
            canBeAnalyzedByAnalystFromKsyos = isPatientOlderThanEighteenOnDate(
                props.patient.birthDate,
                props.procedure.customControlData.configurationDatetime
            );
        }

        if (props.procedure && canBeAnalyzedByAnalystFromKsyos && props.procedure.holterAnalystOrganizationId) {
            return (
                <PractitionerAndOrganizationActors
                    adminProcess={{ ...props }}
                    entity={ProcessEntity.procedure}
                    label={translateKey('processDetails.analyst')}
                    organization={props.procedure.holterAnalystOrganization}
                    organizationId={props.procedure.holterAnalystOrganizationId}
                    practitioner={props.procedure.holterAnalystPractitioner}
                    practitionerId={props.procedure.holterAnalystPractitionerId}
                />
            );
        }

        if (
            props.procedure &&
            props.procedure.status !== ProcedureStatus.inProgress &&
            props.procedure.customControlData.analystOrganizationType
        ) {
            return (
                <HorizontalFormGroupRead>
                    <FormLabel>{translateKey('processDetails.analyst')}</FormLabel>
                    <InputContainer>
                        <FormParagraph>{getAnalyst()}</FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
            );
        }

        if (
            props.procedure &&
            canBeAnalyzedByAnalystFromKsyos &&
            holterAnalystOrganization &&
            holterAnalystOrganization.id
        ) {
            return (
                <PractitionerAndOrganizationActors
                    adminProcess={{ ...props }}
                    entity={ProcessEntity.procedure}
                    label={translateKey('processDetails.analyst')}
                    organization={holterAnalystOrganization}
                    organizationId={holterAnalystOrganization.id}
                    practitioner={null}
                    practitionerId={null}
                />
            );
        }

        return (
            <HorizontalFormGroupRead>
                <FormLabel>{translateKey('processDetails.analyst')}</FormLabel>
                <InputContainer>
                    <FormParagraph>{''}</FormParagraph>
                </InputContainer>
            </HorizontalFormGroupRead>
        );
    }

    function renderGenerateConfiguredByKsyos() {
        if (
            hasButtons &&
            props.procedure?.status === ProcedureStatus.active &&
            props.procedure?.customControlStatus === CustomControlStatus.withPatient &&
            props.procedure?.customControlData.recorderType === RecorderType.bittium
        ) {
            const url = `/api/makeConfiguredByKsyos/${props.id}/ConfiguredByKsyos.dat`;

            return (
                <AnchorButton
                    color="secondary"
                    large={true}
                    openInNewTab={true}
                    outlined={true}
                    to={url}
                >
                    {translateKey('processDetails.generateConfiguredByKsyos')}
                </AnchorButton>
            );
        }

        return null;
    }

    function renderGenerateUploadJson() {
        if (
            hasButtons &&
            props.procedure?.status === ProcedureStatus.active &&
            props.procedure?.customControlStatus === CustomControlStatus.uploading &&
            props.procedure?.customControlData.recorderType === RecorderType.bittium
        ) {
            return (
                <>
                    <Button
                        color="secondary"
                        large={true}
                        onClick={() => generateUploadJson()}
                        outlined={true}
                    >
                        {translateKey('processDetails.generateUploadJson')}
                    </Button>

                    {fillInUploadJsonAlert && (
                        <Alert appearance="info">{translateKey('processDetails.checkUploadJson')}</Alert>
                    )}
                </>
            );
        }

        return null;
    }

    function generateUploadJson() {
        const url = `/api/makeUploadJson/${props.id}/upload.json`;
        window.open(url, '_blank');
        setFillInUploadJsonAlert(true);
    }

    async function retrieveHolterAnalystOrganization() {
        if (props.procedure && props.procedure.performerOrganizationId) {
            const url = `/api/serviceOrganizationSettings?organizationId=${props.procedure.performerOrganizationId}&serviceId=${props.serviceId}&isActive=true`;
            const result = await fetchJson(url, 'GET');
            if (result.json.items.length > 0 && result.json.items[0].ksyosHolterAnalystOrganization) {
                setHolterAnalystOrganization(result.json.items[0].ksyosHolterAnalystOrganization);
            }
        }
    }

    function getRecorderDescription() {
        if (!props.procedure || props.procedure.status === ProcedureStatus.inProgress) {
            return '';
        }

        switch (props.procedure.customControlData.recorderType) {
            case RecorderType.bittium:
                return `Bittium ${props.procedure.customControlData.holterSerialNumber}`;
            case RecorderType.fysiologic:
                return `H2 ${props.procedure.customControlData.holterSerialNumber}`;
            default:
                return props.txcsProcesId ? '' : 'H2';
        }
    }

    function getAnalyst() {
        switch (props.procedure!.customControlData.analystOrganizationType) {
            case AnalystOrganizationType.fysiologic:
                return 'Fysiologic';
            case AnalystOrganizationType.meddis:
                return 'Meddis';
            case AnalystOrganizationType.meddisBecauseOfAge:
                return 'Meddis (i.v.m. leeftijd patiënt < 18 jaar)';
            case AnalystOrganizationType.ksyosAnalyst:
                return 'Ksyos analist organisatie';
            case AnalystOrganizationType.dgtlHealth:
                return 'DGTL Health';
            case AnalystOrganizationType.dgtlHealthBecauseOfDuration:
                return 'DGTL Health (i.v.m. opname langer dan duur onderzoek)';
            default:
                switch (props.procedure!.customControlData.recorderType) {
                    case RecorderType.bittium:
                        return 'Meddis';
                    case RecorderType.fysiologic:
                        return 'Fysiologic';
                    default:
                        return props.txcsProcesId ? '' : 'Fysiologic';
                }
        }
    }

    function getFileCount(customControlData: any) {
        let fileCount = 0;
        if (customControlData.files && customControlData.files.length) {
            fileCount = customControlData.files.length;
        }

        if (customControlData.diaryStorageName) {
            fileCount++;
        }

        if (customControlData.ecgStorageName) {
            fileCount++;
        }

        if (customControlData.xmlStorageName) {
            fileCount++;
        }

        return fileCount || '';
    }

    function getSentData() {
        const progressOrDate = getUploadProgressOrRecorderSentDateTime(
            props.procedure && props.procedure.customControlData
        );
        if (progressOrDate) {
            return progressOrDate.date ? (
                <AdminProcessNullableFormattedDatetime value={progressOrDate.date} />
            ) : (
                progressOrDate.progress
            );
        }

        return null;
    }

    function getDuration() {
        const duration = getHolterOrEcgExaminationDuration(
            props.isDirectProcedureAdded,
            props.customControlData,
            props.procedure && props.procedure.customControlData
        );
        switch (duration) {
            case HolterCardioExaminationDuration.oneDay:
                return '1 ' + translateKey('common.date_day');
            case HolterCardioExaminationDuration.twoDays:
                return '2 ' + translateKey('common.date_days');
            case HolterCardioExaminationDuration.threeDays:
                return '3 ' + translateKey('common.date_days');
            case EventEcgCardioExaminationDuration.sevenDays:
                return '7 ' + translateKey('common.date_days');
            case EventEcgCardioExaminationDuration.fourteenDays:
                return '14 ' + translateKey('common.date_days');
            default:
                break;
        }
    }

    function getHolterOrEcgExaminationDuration(
        isDirectProcedureAdded: boolean,
        requestCustomControlData: {
            eventEcgCardioExaminationDuration: EventEcgCardioExaminationDuration | null;
            holterCardioExaminationDuration: HolterCardioExaminationDuration | null;
        } | null,
        procedureCustomControlData: {
            eventEcgCardioExaminationDuration: EventEcgCardioExaminationDuration | null;
            holterCardioExaminationDuration: HolterCardioExaminationDuration | null;
        } | null
    ): HolterCardioExaminationDuration | EventEcgCardioExaminationDuration | null {
        let holterCardioExaminationDuration =
            requestCustomControlData && requestCustomControlData.holterCardioExaminationDuration;
        let eventEcgCardioExaminationDuration =
            requestCustomControlData && requestCustomControlData.eventEcgCardioExaminationDuration;
        if (isDirectProcedureAdded && procedureCustomControlData) {
            holterCardioExaminationDuration = procedureCustomControlData.holterCardioExaminationDuration;
            eventEcgCardioExaminationDuration = procedureCustomControlData.eventEcgCardioExaminationDuration;
        }

        return holterCardioExaminationDuration || eventEcgCardioExaminationDuration;
    }

    function getDiaryType() {
        if (!props.procedure || !props.procedure.customControlData.diaryType) {
            return translateKey('processDetails.unknown');
        }
        switch (props.procedure.customControlData.diaryType) {
            case DiaryTypes.none:
                return translateKey('processDetails.notAvailable');
            case DiaryTypes.paperFax:
                return translateKey('holter.fax');
            case DiaryTypes.paperUpload:
                return translateKey('processDetails.paper');
            default:
                throw new Error('Incorrect diary type');
        }
    }

    function showNewUploadReport() {
        return (
            !!props.procedure?.customControlData.files &&
            props.procedure.customControlData.files.length > 0 &&
            !!props.procedure.customControlData.files[0].resendDateTime
        );
    }

    function getUploadProgressOrRecorderSentDateTime(
        customControlData: { recordingSentDatetime?: string; uploadProgress?: number } | undefined | null
    ): { date?: string; progress?: string } | undefined {
        if (customControlData && customControlData.uploadProgress) {
            if (customControlData.uploadProgress <= 95) {
                return { progress: `${customControlData.uploadProgress}%` };
            } else if (customControlData.uploadProgress === 100 && customControlData.recordingSentDatetime) {
                return { date: customControlData.recordingSentDatetime };
            }

            return { progress: '95%' };
        }
    }
}
